<template>
  <vue-pdf-app 
    tabIndex="0"
    :pdf="pdf" 
    :config="config"
    @open="openHandler"
    @pages-rendered="pagesRenderedHandler"
    @after-created="afterCreatedHandler"
    >
  </vue-pdf-app>
</template>

<script>
// VuePdfApp component is registered in src/main.js

export default {
  name: 'PDFViewerApp',
  props: {
    pdf: {
      default: null,
    },
    name: {
      type: String,
      default: 'documento.pdf',
    },
    search: {
      type: String,
      default: null,
    },
    containerWidth: {
      type: Number,
      default: -1,
    },
  },
  mounted () {
    // Raíz del visor PDF; toda la manipulación se scopea aquí para no afectar
    // a otros componentes de la aplicación.
    const pdfRoot = this.$el.querySelector('.pdf-app') || this.$el

    // WCAG 2.4.3 Focus Order — Corregir el orden visual del toolbar superior.
    // El vendor renderiza Left → Right → Middle en el DOM, pero visualmente
    // Middle está entre Left y Right. Reordenamos el DOM para que el orden
    // de tabulación coincida con el visual.
    this._reorderToolbar(pdfRoot)

    // WCAG 2.4.3 — Sustituir todos los tabindex positivos por 0. Se hace en
    // caliente sobre elementos existentes y se observa el DOM para normalizar
    // también los que se crean dinámicamente (thumbnails, outline, attachments,
    // contadores de findbar, etc.).
    this._setupTabindexNormalization(pdfRoot)

    // WCAG 2.1.1 Keyboard — pdf.js registra un handler global de keydown en
    // window que trata Enter/Space como "pasar de página" o scroll y solo
    // exime INPUT/TEXTAREA/SELECT (no BUTTON ni <a>). Al pulsar Enter/Space
    // sobre un botón del toolbar el vendor llama preventDefault y la
    // activación nativa del botón se cancela, o bien deja que Space scrollee
    // la vista. Bloqueamos la propagación en captura para elementos
    // interactivos, sin evitar el default → el botón se activa como siempre.
    this._setupKeyboardActivationFix(pdfRoot)

    document.getElementById('pageNumber')
      .setAttribute('aria-label', 'Página del documento')
    document.getElementById('scaleSelect')
      .setAttribute('aria-label', 'Tamaño del documento')

    // WCAG fix: Permitir abrir/cerrar el menú secundario (tres puntos) con teclado
    this._setupSecondaryToolbarKeyboardAccess()

    // WCAG fix: Mover el foco al botón "Cerrar" cuando se abre el diálogo de Propiedades del documento
    this._setupDocumentPropertiesFocusTrap()

    // WCAG 2.4.3 / 2.1.2 — Devolver el foco al botón "Buscar" al cerrar la
    // barra de búsqueda; añadir soporte de Escape.
    this._setupFindbarFocusReturn()

    // WCAG 2.4.3 — Devolver el foco al toggle de la barra lateral cuando
    // se cierra estando el foco dentro (sus botones pasan a display:none).
    this._setupSidebarFocusReturn()

    // WCAG 2.4.3 — Patrón WAI-ARIA "Disclosure Widget" para findbar y sidebar.
    // Los popups están antes del toolbar en el DOM (findbar es hijo de
    // #mainContainer, sidebar de #outerContainer). Con solo tabindex=0 el foco
    // saltaría "hacia atrás" al recorrerlos. Interceptamos Tab/Shift+Tab en los
    // bordes de cada popup y en el elemento vecino del trigger en el toolbar
    // para redirigir el foco de forma coherente con el orden visual.
    this._setupPopupTabOrder({
      trigger: document.getElementById('viewFind'),
      popup: document.getElementById('findbar'),
      isPopupOpen: () => {
        const fb = document.getElementById('findbar')
        return !!(fb && !fb.classList.contains('hidden'))
      },
    })
    this._setupPopupTabOrder({
      trigger: document.getElementById('sidebarToggle'),
      popup: document.getElementById('sidebarContainer'),
      isPopupOpen: () => {
        const oc = document.getElementById('outerContainer')
        return !!(oc && oc.classList.contains('sidebarOpen'))
      },
    })
  },
  beforeDestroy () {
    if (this._secondaryToolbarObserver) {
      this._secondaryToolbarObserver.disconnect()
    }
    if (this._docPropsObserver) {
      this._docPropsObserver.disconnect()
    }
    if (this._docPropsContainerObserver) {
      this._docPropsContainerObserver.disconnect()
    }
    if (this._tabindexObserver) {
      this._tabindexObserver.disconnect()
    }
    if (this._findbarObserver) {
      this._findbarObserver.disconnect()
    }
    if (this._sidebarObserver) {
      this._sidebarObserver.disconnect()
    }
    if (this._popupTabOrderCleanups) {
      this._popupTabOrderCleanups.forEach((fn) => fn())
      this._popupTabOrderCleanups = null
    }
    if (this._keyboardFixCleanup) {
      this._keyboardFixCleanup()
      this._keyboardFixCleanup = null
    }
  },  
  computed: {
    isCompactLayout () {
      if (this.containerWidth === -1) return false
      return this.containerWidth > 0 && this.containerWidth < 600
    },
    isMiniLayout () {
      if (this.containerWidth === -1) return false
      return this.containerWidth > 0 && this.containerWidth < 400
    },
    config () {
      return {
        sidebar: {
          viewThumbnail: true,
          viewOutline: true,
          viewAttachments: true,
        },
        secondaryToolbar: {
          secondaryPresentationMode: true,
          secondaryOpenFile: false,
          secondaryPrint: false,
          secondaryDownload: false,
          secondaryViewBookmark: false,
          firstPage: true,
          lastPage: true,
          pageRotateCw: true,
          pageRotateCcw: true,
          cursorSelectTool: true,
          cursorHandTool: true,
          scrollVertical: true,
          scrollHorizontal: true,
          scrollWrapped: true,
          spreadNone: true,
          spreadOdd: true,
          spreadEven: true,
          documentProperties: true,
        },
        toolbar: {
          toolbarViewerLeft: {
            findbar: true,
            previous: false,
            next: false,
            pageNumber: !this.isMiniLayout,
          },
          toolbarViewerRight: {
            presentationMode: true,
            openFile: false,
            print: false,
            download: false,
            viewBookmark: false,
          },
          toolbarViewerMiddle: {
            zoomOut: true,
            zoomIn: true,
            scaleSelectContainer: !this.isCompactLayout,
          },
        },
        errorWrapper: true,
      }
    }
  },
  methods: {
    afterCreatedHandler (app) {
      const oldOpen = app.open.bind(app)
      app.open = function (file, args = {}) {
        return oldOpen(file, {
          ...args,
          cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.2.228/cmaps/',
          cMapPacked: true,
        })
      }
    },
    openHandler (pdfApp) {
      // window._pdfApp = pdfApp
      // console.log(pdfApp)
      pdfApp.setTitleUsingUrl(this.name)
      document.body.classList.remove('loadingInProgress')
    },
    pagesRenderedHandler (pdfApp) {
      pdfApp.setTitleUsingUrl(this.name)
      setTimeout(() => (pdfApp.pdfViewer.currentPageNumber = 1))
      this.searchContent()
    },
    searchContent () {
      if (this.search) {
        document.getElementById('viewFind').click()
        document.getElementById('findInput').value= this.search
        document.getElementById('findHighlightAll').click()
      }
      this.$emit('pdfViewerOpenHandler')
    },
    // WCAG 2.4.3 Focus Order — Reordenar el DOM del toolbar superior para que
    // el orden de tabulación coincida con el orden visual: Left → Middle → Right.
    // El vendor renderiza Left → Right → Middle porque usa CSS float/absolute
    // para colocar Middle en el centro, pero eso rompe el orden de foco.
    _reorderToolbar (pdfRoot) {
      const viewer = pdfRoot.querySelector('#toolbarViewer')
      const middle = pdfRoot.querySelector('#toolbarViewerMiddle')
      const right = pdfRoot.querySelector('#toolbarViewerRight')
      if (viewer && middle && right && middle.nextElementSibling !== right) {
        viewer.insertBefore(middle, right)
      }
    },
    // WCAG 2.4.3 — Normaliza cualquier tabindex positivo a 0 dentro del visor.
    // Se ejecuta en caliente sobre elementos existentes y observa el DOM para
    // cubrir elementos creados dinámicamente (thumbnails, outline items,
    // attachments, contadores/labels del findbar, etc.).
    _setupTabindexNormalization (pdfRoot) {
      const normalize = (root) => {
        root.querySelectorAll('[tabindex]').forEach((el) => {
          const val = parseInt(el.getAttribute('tabindex'), 10)
          if (val > 0) el.setAttribute('tabindex', '0')
        })
      }
      normalize(pdfRoot)

      this._tabindexObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'tabindex') {
            const el = m.target
            const val = parseInt(el.getAttribute('tabindex'), 10)
            if (val > 0) el.setAttribute('tabindex', '0')
          } else if (m.type === 'childList') {
            m.addedNodes.forEach((node) => {
              if (node.nodeType !== 1) return
              if (node.hasAttribute && node.hasAttribute('tabindex')) {
                const val = parseInt(node.getAttribute('tabindex'), 10)
                if (val > 0) node.setAttribute('tabindex', '0')
              }
              if (node.querySelectorAll) normalize(node)
            })
          }
        }
      })
      this._tabindexObserver.observe(pdfRoot, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex'],
      })
    },
    // WCAG 2.1.1 Keyboard — Evita que el handler global de keydown del vendor
    // pdf.js (registrado en window) consuma Enter/Space cuando el foco está
    // en un botón/link/select del visor. El vendor solo exime INPUT/TEXTAREA/
    // SELECT, por lo que sobre botones dispara scroll/pasar de página y/o
    // llama a preventDefault, rompiendo la activación nativa del control.
    //
    // Escuchamos en captura desde .pdf-app y llamamos a stopPropagation() para
    // que el evento no alcance el listener de window. stopPropagation NO evita
    // el default del navegador → el botón/link se activa con normalidad.
    _setupKeyboardActivationFix (pdfRoot) {
      const isInteractive = (el) => {
        if (!el || el.nodeType !== 1) return false
        const tag = el.tagName
        if (tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return true
        return el.getAttribute && el.getAttribute('role') === 'button'
      }
      const handler = (e) => {
        // "Spacebar" es el nombre legacy en IE/Edge antiguo.
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return
        if (isInteractive(e.target)) {
          e.stopPropagation()
        }
      }
      pdfRoot.addEventListener('keydown', handler, true)
      this._keyboardFixCleanup = () => {
        pdfRoot.removeEventListener('keydown', handler, true)
      }
    },
    // WCAG 2.4.3 / 2.1.2 — El vendor focusa #findInput al abrir el findbar,
    // pero al cerrar no devuelve el foco a #viewFind. Además, Escape dentro
    // del findbar no lo cierra. Añadimos ambos comportamientos.
    _setupFindbarFocusReturn () {
      const findbar = document.getElementById('findbar')
      const viewFind = document.getElementById('viewFind')
      if (!findbar || !viewFind) return

      let wasHidden = findbar.classList.contains('hidden')
      this._findbarObserver = new MutationObserver(() => {
        const isHidden = findbar.classList.contains('hidden')
        if (isHidden && !wasHidden) {
          // Se acaba de cerrar: si el foco está dentro del findbar (o en body
          // tras un blur), devolverlo al botón que lo abrió.
          const active = document.activeElement
          if (!active || active === document.body || findbar.contains(active)) {
            requestAnimationFrame(() => viewFind.focus())
          }
        }
        wasHidden = isHidden
      })
      this._findbarObserver.observe(findbar, {
        attributes: true,
        attributeFilter: ['class'],
      })

      // Escape dentro del findbar → cerrar y devolver foco.
      findbar.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          if (!findbar.classList.contains('hidden')) {
            viewFind.click()
          }
        }
      })
    },
    // WCAG 2.4.3 — Cuando el sidebar se cierra y el foco está dentro (sus
    // botones pasan a display:none), devolverlo al toggle para no perderlo.
    _setupSidebarFocusReturn () {
      const sidebar = document.getElementById('sidebarContainer')
      const sidebarToggle = document.getElementById('sidebarToggle')
      const outerContainer = document.getElementById('outerContainer')
      if (!sidebar || !sidebarToggle || !outerContainer) return

      // El sidebar se abre/cierra togglando la clase 'sidebarOpen' o
      // 'sidebarMoving' en #outerContainer.
      let wasOpen = outerContainer.classList.contains('sidebarOpen')
      this._sidebarObserver = new MutationObserver(() => {
        const isOpen = outerContainer.classList.contains('sidebarOpen')
        if (!isOpen && wasOpen) {
          const active = document.activeElement
          if (!active || active === document.body || sidebar.contains(active)) {
            requestAnimationFrame(() => sidebarToggle.focus())
          }
        }
        wasOpen = isOpen
      })
      this._sidebarObserver.observe(outerContainer, {
        attributes: true,
        attributeFilter: ['class'],
      })
    },
    // WCAG 2.4.3 — Devuelve los elementos enfocables visibles y habilitados
    // dentro de un contenedor, en orden DOM.
    _getVisibleFocusable (container) {
      if (!container) return []
      const selector = [
        'button:not([disabled])',
        'a[href]',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')
      return Array.from(container.querySelectorAll(selector)).filter((el) => {
        if (el.hidden || el.disabled) return false
        // Excluye elementos con display:none, visibility:hidden o fuera del layout.
        if (el.getClientRects().length === 0) return false
        return window.getComputedStyle(el).visibility !== 'hidden'
      })
    },
    // WCAG 2.4.3 — Devuelve el primer enfocable del toolbar superior que
    // aparece DESPUÉS del elemento dado (orden visual = orden DOM tras el
    // reordenamiento aplicado por _reorderToolbar).
    _getNextInToolbar (el) {
      const toolbar = document.getElementById('toolbarViewer')
      if (!toolbar || !el) return null
      const focusables = this._getVisibleFocusable(toolbar)
      const idx = focusables.indexOf(el)
      return idx >= 0 && idx < focusables.length - 1 ? focusables[idx + 1] : null
    },
    // WCAG 2.4.3 — Patrón "Disclosure Widget" (WAI-ARIA APG).
    //
    // Los popups (findbar, sidebar) están en el DOM ANTES del toolbar. Sin
    // interceptar Tab/Shift+Tab el foco iría hacia atrás al recorrerlos, lo que
    // rompe SC 2.4.3 (orden de foco lógico).
    //
    // Redirigimos el foco en 4 puntos frontera:
    //   1. Tab desde el trigger (popup abierto) → primer elemento del popup.
    //   2. Shift+Tab desde el primer elemento del popup → trigger.
    //   3. Tab desde el último elemento del popup → siguiente del toolbar.
    //   4. Shift+Tab desde el "siguiente del toolbar" (popup abierto) → último
    //      elemento del popup.
    //
    // Los elementos enfocables del popup se recalculan en cada Tab porque
    // pueden aparecer/desaparecer dinámicamente (thumbnails, outline, etc.).
    _setupPopupTabOrder ({ trigger, popup, isPopupOpen }) {
      if (!trigger || !popup) return

      const onTriggerKeydown = (e) => {
        if (e.key !== 'Tab' || e.shiftKey) return
        if (!isPopupOpen()) return
        const first = this._getVisibleFocusable(popup)[0]
        if (first && first !== trigger) {
          e.preventDefault()
          first.focus()
        }
      }

      const onPopupKeydown = (e) => {
        if (e.key !== 'Tab') return
        const focusables = this._getVisibleFocusable(popup)
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        const active = document.activeElement
        if (e.shiftKey && active === first) {
          e.preventDefault()
          trigger.focus()
        } else if (!e.shiftKey && active === last) {
          const resume = this._getNextInToolbar(trigger)
          if (resume) {
            e.preventDefault()
            resume.focus()
          }
        }
      }

      // Handler en el toolbar para el punto (4): capturamos Shift+Tab desde el
      // "siguiente al trigger" y redirigimos al último elemento del popup.
      const toolbar = document.getElementById('toolbarViewer')
      const onToolbarKeydown = (e) => {
        if (e.key !== 'Tab' || !e.shiftKey) return
        if (!isPopupOpen()) return
        const resume = this._getNextInToolbar(trigger)
        if (!resume || e.target !== resume) return
        const focusables = this._getVisibleFocusable(popup)
        const last = focusables[focusables.length - 1]
        if (last) {
          e.preventDefault()
          last.focus()
        }
      }

      trigger.addEventListener('keydown', onTriggerKeydown)
      popup.addEventListener('keydown', onPopupKeydown)
      if (toolbar) toolbar.addEventListener('keydown', onToolbarKeydown)

      this._popupTabOrderCleanups = this._popupTabOrderCleanups || []
      this._popupTabOrderCleanups.push(() => {
        trigger.removeEventListener('keydown', onTriggerKeydown)
        popup.removeEventListener('keydown', onPopupKeydown)
        if (toolbar) toolbar.removeEventListener('keydown', onToolbarKeydown)
      })
    },
    _setupSecondaryToolbarKeyboardAccess () {
      const toggleBtn = document.getElementById('secondaryToolbarToggle')
      const toolbar = document.getElementById('secondaryToolbar')
      const container = document.getElementById('secondaryToolbarButtonContainer')
      if (!toggleBtn || !toolbar || !container) return

      toggleBtn.setAttribute('aria-haspopup', 'true')
      toggleBtn.setAttribute('aria-expanded', 'false')
      toolbar.setAttribute('role', 'menu')
      toolbar.setAttribute('aria-label', 'Menú de herramientas')

      // Marcar los botones visibles como menuitems y asegurar focusabilidad
      container.querySelectorAll('button').forEach(btn => {
        btn.setAttribute('role', 'menuitem')
        btn.setAttribute('tabindex', '-1')
      })

      // Función helper para obtener los botones visibles y habilitados del menú
      const getVisibleButtons = () => {
        return Array.from(container.querySelectorAll('button')).filter(btn => {
          const style = window.getComputedStyle(btn)
          return style.display !== 'none' && !btn.hidden && !btn.disabled
        })
      }

      // Observar cuándo se abre/cierra el menú secundario para mover el foco
      this._secondaryToolbarObserver = new MutationObserver(() => {
        const isOpen = !toolbar.classList.contains('hidden')
        toggleBtn.setAttribute('aria-expanded', String(isOpen))
        if (isOpen) {
          // Esperar a que el layout esté completo antes de mover el foco
          requestAnimationFrame(() => {
            const buttons = getVisibleButtons()
            if (buttons.length > 0) {
              buttons[0].setAttribute('tabindex', '0')
              buttons[0].focus()
            }
          })
        } else {
          // Restaurar tabindex cuando se cierra
          container.querySelectorAll('button').forEach(btn => {
            btn.setAttribute('tabindex', '-1')
          })
        }
      })
      this._secondaryToolbarObserver.observe(toolbar, { attributes: true, attributeFilter: ['class'] })

      // Focus trapping y navegación con teclado dentro del menú
      container.addEventListener('keydown', (e) => {
        const buttons = getVisibleButtons()
        const currentIndex = buttons.indexOf(document.activeElement)

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault()
            buttons.forEach(b => b.setAttribute('tabindex', '-1'))
            buttons[(currentIndex + 1) % buttons.length].setAttribute('tabindex', '0')
            buttons[(currentIndex + 1) % buttons.length].focus()
            break
          case 'ArrowUp':
            e.preventDefault()
            buttons.forEach(b => b.setAttribute('tabindex', '-1'))
            buttons[(currentIndex - 1 + buttons.length) % buttons.length].setAttribute('tabindex', '0')
            buttons[(currentIndex - 1 + buttons.length) % buttons.length].focus()
            break
          case 'Home':
            e.preventDefault()
            buttons.forEach(b => b.setAttribute('tabindex', '-1'))
            buttons[0].setAttribute('tabindex', '0')
            buttons[0].focus()
            break
          case 'End':
            e.preventDefault()
            buttons.forEach(b => b.setAttribute('tabindex', '-1'))
            buttons[buttons.length - 1].setAttribute('tabindex', '0')
            buttons[buttons.length - 1].focus()
            break
          case 'Escape':
            e.preventDefault()
            toggleBtn.click()
            toggleBtn.focus()
            break
          case 'Tab':
            e.preventDefault()
            if (e.shiftKey) {
              buttons.forEach(b => b.setAttribute('tabindex', '-1'))
              buttons[(currentIndex - 1 + buttons.length) % buttons.length].setAttribute('tabindex', '0')
              buttons[(currentIndex - 1 + buttons.length) % buttons.length].focus()
            } else {
              buttons.forEach(b => b.setAttribute('tabindex', '-1'))
              buttons[(currentIndex + 1) % buttons.length].setAttribute('tabindex', '0')
              buttons[(currentIndex + 1) % buttons.length].focus()
            }
            break
        }
      })
    },
    _setupDocumentPropertiesFocusTrap () {
      const overlay = document.getElementById('documentPropertiesOverlay')
      const overlayContainer = document.getElementById('overlayContainer')
      if (!overlay) return

      const closeBtn = document.getElementById('documentPropertiesClose')
      if (!closeBtn) return

      // Asegurar accesibilidad del diálogo
      overlay.setAttribute('role', 'dialog')
      overlay.setAttribute('aria-modal', 'true')
      overlay.setAttribute('aria-label', 'Propiedades del documento')
      closeBtn.setAttribute('tabindex', '0')

      const toggleBtn = document.getElementById('secondaryToolbarToggle')

      // Observar el overlay y su contenedor padre
      this._docPropsObserver = new MutationObserver(() => {
        const overlayVisible = !overlay.classList.contains('hidden')
        const containerVisible = !overlayContainer || !overlayContainer.classList.contains('hidden')
        if (overlayVisible && containerVisible) {
          requestAnimationFrame(() => {
            closeBtn.focus()
          })
        }
      })
      this._docPropsObserver.observe(overlay, { attributes: true, attributeFilter: ['class'] })
      if (overlayContainer) {
        this._docPropsContainerObserver = new MutationObserver(() => {
          const overlayVisible = !overlay.classList.contains('hidden')
          const containerVisible = !overlayContainer.classList.contains('hidden')
          if (overlayVisible && containerVisible) {
            requestAnimationFrame(() => {
              closeBtn.focus()
            })
          }
          // Al cerrar el diálogo, devolver el foco al botón de tres puntos
          if (!overlayVisible || !containerVisible) {
            if (toggleBtn) {
              requestAnimationFrame(() => {
                toggleBtn.focus()
              })
            }
          }
        })
        this._docPropsContainerObserver.observe(overlayContainer, { attributes: true, attributeFilter: ['class'] })
      }

      // Focus trapping dentro del diálogo de propiedades
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          e.preventDefault()
          closeBtn.focus()
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          closeBtn.click()
        }
      })
    },
  }
}
</script>

<style scoped>

.pdf-app{
  --pdf-app-background-color:var(--primary-50);
  --pdf-sidebar-content-color:rgba(0, 0, 0, 0.1);
  --pdf-toolbar-sidebar-color:var(--primary-100);
  --pdf-toolbar-color:var(--primary-100);
  --pdf-loading-bar-color:var(--secondary-DEFAULT);
  --pdf-loading-bar-secondary-color: var(--primary-DEFAULT);
  --pdf-find-results-count-color:var(--primary-DEFAULT);
  --pdf-find-results-count-font-color:#525252;
  --pdf-find-message-font-color:#525252;
  --pdf-not-found-color:#f66;
  --pdf-split-toolbar-button-separator-color:var(--darkblue-400);
  --pdf-toolbar-font-color:var(--secondary-DEFAULT);
  --pdf-button-hover-font-color:var(--primary-800);
  --pdf-button-toggled-color:var(--primary-200);
  --pdf-horizontal-toolbar-separator-color:var(--darkblue-400);
  --pdf-input-color:var(--primary-200);
  --pdf-input-font-color:var(--secondary-DEFAULT);
  --pdf-find-input-placeholder-font-color:var(--primary-900);
  --pdf-thumbnail-selection-ring-color:hsla(0, 0, 55%, 0.15);
  --pdf-thumbnail-selection-ring-selected-color:hsla(0, 0%, 50%, 0.3);
  --pdf-error-wrapper-color:#f55;
  --pdf-error-more-info-color:var(--secondary-DEFAULT);
  --pdf-error-more-info-font-color:#000;
  --pdf-overlay-container-color:rgba(0,0,0,0.2);
  --pdf-overlay-container-dialog-color:var(--primary-100);
  --pdf-overlay-container-dialog-font-color:var(--secondary-DEFAULT);
  --pdf-overlay-container-dialog-separator-color:var(--darkblue-400);
  --pdf-dialog-button-font-color:var(--secondary-DEFAULT);
  --pdf-dialog-button-color:var(--primary-200);
  }

.dark .pdf-app{
  --pdf-app-background-color:var(--darkblue-DEFAULT);
  --pdf-sidebar-content-color:rgba(0, 0, 0, 0.1);
  --pdf-toolbar-sidebar-color:var(--darkblue-600);
  --pdf-toolbar-color:var(--darkblue-600);
  --pdf-loading-bar-color:var(--darkblue-500);
  --pdf-loading-bar-secondary-color: var(--primary-DEFAULT);
  --pdf-find-results-count-color:var(--darkblue-100);
  --pdf-find-results-count-font-color:#525252;
  --pdf-find-message-font-color:#525252;
  --pdf-not-found-color:#f66;
  --pdf-split-toolbar-button-separator-color:#fff;
  --pdf-toolbar-font-color:var(--darkblue-100);
  --pdf-button-hover-font-color:var(--primary-800);
  --pdf-button-toggled-color:var(--darkblue-500);
  --pdf-horizontal-toolbar-separator-color:#fff;
  --pdf-input-color:var(--darkblue-500);
  --pdf-input-font-color:var(--darkblue-100);
  --pdf-find-input-placeholder-font-color:#11ece5;
  --pdf-thumbnail-selection-ring-color:hsla(0,0%,100%,0.15);
  --pdf-thumbnail-selection-ring-selected-color:hsla(0, 0%, 100%, 0.3);
  --pdf-error-wrapper-color:#f55;
  --pdf-error-more-info-color:var(--darkblue-100);
  --pdf-error-more-info-font-color:#000;
  --pdf-overlay-container-color:rgba(0,0,0,0.2);
  --pdf-overlay-container-dialog-color:var(--darkblue-600);
  --pdf-overlay-container-dialog-font-color:var(--darkblue-100);
  --pdf-overlay-container-dialog-separator-color:#fff;
  --pdf-dialog-button-font-color:var(--darkblue-100);
  --pdf-dialog-button-color:var(--darkblue-500);
  }
</style>

<!-- WCAG 2.1 AA: estilos de foco visibles para elementos del visor PDF (no scoped para penetrar en vue-pdf-app) -->
<style>
.pdf-app #secondaryToolbarButtonContainer button:focus,
.pdf-app #secondaryToolbarButtonContainer button:focus-visible {
  outline: 2px solid var(--pdf-button-hover-font-color, #11ece5) !important;
  outline-offset: -2px;
}

.pdf-app #documentPropertiesClose:focus,
.pdf-app #documentPropertiesClose:focus-visible {
  outline: 2px solid var(--pdf-button-hover-font-color, #11ece5) !important;
  outline-offset: 2px;
}

.pdf-app #secondaryToolbarToggle:focus,
.pdf-app #secondaryToolbarToggle:focus-visible {
  outline: 2px solid var(--pdf-button-hover-font-color, #11ece5) !important;
  outline-offset: -2px;
}
</style>
